/**
 * issue #79 Phase β - DriveAuthContext (plan §3.1)。
 *
 * driveTokenStore (SSOT) を React の文脈で配信する Provider + hook。
 * 既存 useGoogleDriveSaver / useGoogleDrivePicker は本 Context へ内部委譲し、
 * Drive 認証の状態と操作を Provider 単位で集約する (Phase γ で consumer 統一)。
 *
 * 不変条件:
 * - driveTokenStore の API はそのまま (本 Context は wrap のみ)
 * - 既存 hook の export shape (saveFingering 等) は不変
 * - signInInteractive / getSilentToken は drive.file consent (issue #123: readonly 廃止で file 単一)
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import {
  getDriveToken,
  clearDriveToken,
  getCombinedDriveToken,
  hasValidDriveToken,
  hasFullDriveAccess,
  subscribeDriveToken,
} from '../utils/driveTokenStore';

export interface DriveAuthContextValue {
  /** drive.file token が fresh か (ログイン indicator 用、 issue #123 で file 単一)。 */
  isLoggedIn: boolean;
  /** drive.file token が fresh か (「🟢 全機能可」表示用、 issue #123 で file 単一)。 */
  hasFullAccess: boolean;
  /** silent (popup なし) で drive.file token を取得。 cache hit ならそのまま返す。 */
  getSilentToken: () => Promise<string>;
  /** consent 直行で drive.file token を取得 (user gesture 同期直下、 popup blocker 回避)。 */
  signInInteractive: () => Promise<string>;
  /** drive.file token を捨てて取り直す (401 リトライ用)。 */
  refreshFileToken: () => Promise<string>;
  /** token を破棄してログアウト状態へ。 */
  logout: () => void;
}

const DriveAuthContext = createContext<DriveAuthContextValue | null>(null);

export const DriveAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // driveTokenStore の通知購読 (saveCached / clearDriveToken 経路)。
  // 4 hook インスタンス間で同期するための single source of truth。
  const isLoggedIn = useSyncExternalStore(
    subscribeDriveToken,
    () => hasValidDriveToken(),
  );
  const hasFullAccess = useSyncExternalStore(
    subscribeDriveToken,
    () => hasFullDriveAccess(),
  );

  const getSilentToken = useCallback(
    () => getCombinedDriveToken({ interactive: false }),
    [],
  );

  const signInInteractive = useCallback(
    () => getCombinedDriveToken({ interactive: true }),
    [],
  );

  const refreshFileToken = useCallback(async () => {
    clearDriveToken('file');
    return await getDriveToken('file');
  }, []);

  const logout = useCallback(() => {
    clearDriveToken();
  }, []);

  const value = useMemo<DriveAuthContextValue>(
    () => ({
      isLoggedIn,
      hasFullAccess,
      getSilentToken,
      signInInteractive,
      refreshFileToken,
      logout,
    }),
    [
      isLoggedIn,
      hasFullAccess,
      getSilentToken,
      signInInteractive,
      refreshFileToken,
      logout,
    ],
  );

  return (
    <DriveAuthContext.Provider value={value}>
      {children}
    </DriveAuthContext.Provider>
  );
};

export function useDriveAuth(): DriveAuthContextValue {
  const ctx = useContext(DriveAuthContext);
  if (!ctx) {
    throw new Error('useDriveAuth() must be used within <DriveAuthProvider>');
  }
  return ctx;
}
